import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
} from "@angular/core";
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { MatTabGroup } from "@angular/material/tabs";
import { CONSTANT } from "src/app/constant";
import { AssessmentEvaludationReq, OBAttempt, OBComments } from "src/app/models/models";
import { DraftService } from "src/app/shared/services/draft.service";
import { SharedService } from "src/app/shared/services/shared.service";

@Component({
	selector: "app-maneuver-form",
	templateUrl: "./maneuver-form.component.html",
	styleUrls: ["./maneuver-form.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ManeuverFormComponent implements OnInit {
	maneuverForm!: UntypedFormGroup;
	parentForm!: UntypedFormArray;
	parentFormArrayIndex = 0;
	panelOpenState = false;
	currentSliderValue = 0;
	loadingCount = 0;
	resetRepeat = 0;
	CONSTANT = CONSTANT;
	formType!: string;

	@Input() isManeuverVisible = true;
	@Input() expensionShow = true;
	@Input() isSafetyOutcomeVisible = false;
	@Input() tabIndex!: MatTabGroup;
	@Input() obComments!: Array<OBComments>;

	@Input("maneuverForm") set maneuverFormInput(value: any) {
		this.maneuverForm = value as UntypedFormGroup;
		// generate all OB from master data
		this.TS1_OB_LIST = this.sharedService.generateSortedOBList(
			this.maneuverForm.value.formType.code ?? this.maneuverForm.value.formType,
		);

		this.formType = value.value.formType?.code || value.value.formType;
	}

	@Input() maneuverAction?: "create-form" | "edit-form";
	@Input() readOnly = false;
	@Output() selectedOBList = new EventEmitter<any>();
	@Output() resetCurrentManeuver = new EventEmitter<any>();

	TS1_OB_LIST: Array<any> = [];
	tempFrom: any = {};
	hasValueList: any = [];
	newOBList: any = []; // OB's added on rating

	constructor(
		public sharedService: SharedService,
		private cd: ChangeDetectorRef,
		private draftService: DraftService,
	) {}

	ngOnInit(): void {
		if (!this.maneuverForm) return;

		this.parentForm = this.maneuverForm.parent as UntypedFormArray;
		this.parentFormArrayIndex = this.parentForm
			? this.parentForm.controls.findIndex((subform) => subform === this.maneuverForm)
			: 0;

		this.selectedOBs?.valueChanges.subscribe((val) => {
			if (!val) return;
			if (Array.isArray(val)) {
				for (const v of val) this.selectedOBList.emit(v);
			} else {
				this.selectedOBList.emit(val);
			}
		});
	}

	get inDraftMode(): boolean {
		return this.draftService.inDraftMode;
	}

	ngAfterContentChecked(): void {
		if (!this.maneuverForm) return;

		if (this.maneuverForm.touched && this.loadingCount === 1) {
			this.tempFrom = { ...this.maneuverForm.value };
			if (this.safetyOutcome?.value === -2) {
				this.maneuverForm.setErrors({ invalid: true });
			}
			this.TS1_OB_LIST.forEach((ob: any, index: number) => {
				this.hasValueList[index] = this.hasValue(ob.title);
			});
			this.cd.detectChanges();
		}
		this.loadingCount++;
	}

	hasValue(key: string): boolean {
		const group = this.maneuverForm?.get(key) as UntypedFormGroup | null;
		if (!group) return false;
		const codeValues = group.value ?? {};
		for (const k of Object.keys(codeValues)) {
			if (codeValues[k]?.currentCount > 0) return true;
		}
		return false;
	}

	// ------- many original handlers (trimmed for brevity but unchanged logic) -------
	confirmationModal(data: AssessmentEvaludationReq): void {
		/* your existing implementation */
	}
	temCommentsModal(data: AssessmentEvaludationReq): void {
		/* your existing implementation */
	}
	onRatingChange(value: any) {
		if (this.readOnly) return;
		/* your existing implementation */
	}
	ratingChangeHandler(data: AssessmentEvaludationReq): void {
		/* your existing implementation */
	}

	get maneuver() {
		return this.maneuverForm.get("maneuver")!;
	}
	get maneuverCode() {
		return this.maneuverForm.get("maneuverCode")!;
	}
	get assessmentId() {
		return this.maneuverForm.get("assessmentId")!;
	}
	get selectedOBs() {
		return this.maneuverForm.get("selectedOBs")!;
	}
	get traineeAssessmentId() {
		return this.maneuverForm.get("traineeAssessmentId")!;
	}
	get candidateId() {
		return this.maneuverForm.get("candidateId")!;
	}

	// Category convenience (still usable if needed elsewhere)
	get PRO(): UntypedFormGroup {
		return this.maneuverForm.controls["PRO"] as UntypedFormGroup;
	}
	get COM(): UntypedFormGroup {
		return this.maneuverForm.controls["COM"] as UntypedFormGroup;
	}
	get LTW(): UntypedFormGroup {
		return this.maneuverForm.controls["LTW"] as UntypedFormGroup;
	}
	get PSD(): UntypedFormGroup {
		return this.maneuverForm.controls["PSD"] as UntypedFormGroup;
	}
	get FPA(): UntypedFormGroup {
		return this.maneuverForm.controls["FPA"] as UntypedFormGroup;
	}
	get FPM(): UntypedFormGroup {
		return this.maneuverForm.controls["FPM"] as UntypedFormGroup;
	}
	get SAW(): UntypedFormGroup {
		return this.maneuverForm.controls["SAW"] as UntypedFormGroup;
	}
	get WLM(): UntypedFormGroup {
		return this.maneuverForm.controls["WLM"] as UntypedFormGroup;
	}
	get KNO(): UntypedFormGroup {
		return this.maneuverForm.controls["KNO"] as UntypedFormGroup;
	}

	get safetyOutcome() {
		return this.maneuverForm.get("safetyOutcome");
	}
	get repeat() {
		return this.maneuverForm.get("repeat");
	}

	saveManeuver(value: string): void {
		this.maneuver.patchValue(value);
	}

	onCheckBoxClick($event: any): void {
		if (this.readOnly) return;
		const [control, ob, isAdded, value] = $event;
		if (this.isSafetyOutcomeVisible) {
			this.obCommentsModel(control, ob, isAdded, value);
		} else {
			const action =
				this.maneuverAction === "edit-form" ? CONSTANT.OB_LAST_ACTION.EDIT : CONSTANT.OB_LAST_ACTION.CREATE;

			control?.patchValue({
				...value,
				totalCount: value.currentCount,
				currentCount: value.currentCount,
				lastAction: action,
			});
		}
	}

	reaptConfirmModal(): void {
		if (this.readOnly) return;
		/* your existing implementation */
	}
	obCommentsModel(action: AbstractControl | null, ob: string, isAdded: boolean, newActionValue: OBAttempt): void {
		/* your existing implementation */
	}
	saveAndClear() {
		/* your existing implementation */
	}
	next() {
		/* your existing implementation */
	}
	back() {
		/* your existing implementation */
	}
}
